/**
 * Shared statusPhrase classifiers (SPA crosswalk X5).
 * Prefer park property overlays (blockedReason / deferReason) when callers have them;
 * phrase regex is the common fallback across Drive, Guidance, and Mission Control.
 */

import type { DriveLifecycle, GuidanceNodeStatus } from '../components/ControlBridge/personalLayer/types';

export function normalizeStatusPhrase(phrase: string | null | undefined): string {
  return (phrase ?? '').trim().toLowerCase();
}

export function isDoneStatusPhrase(phrase: string | null | undefined): boolean {
  return /done|complete|closed|posted/.test(normalizeStatusPhrase(phrase));
}

export function isActiveStatusPhrase(phrase: string | null | undefined): boolean {
  return /active|progress|open|ready|in.?flight/.test(normalizeStatusPhrase(phrase));
}

export function isDraftStatusPhrase(phrase: string | null | undefined): boolean {
  return /draft/.test(normalizeStatusPhrase(phrase));
}

export function isBlockedStatusPhrase(phrase: string | null | undefined): boolean {
  return /block/.test(normalizeStatusPhrase(phrase));
}

export function isDeferredStatusPhrase(phrase: string | null | undefined): boolean {
  return /defer/.test(normalizeStatusPhrase(phrase));
}

export function isMovedStatusPhrase(phrase: string | null | undefined): boolean {
  return /moved|transition/.test(normalizeStatusPhrase(phrase));
}

export function isCreatedStatusPhrase(phrase: string | null | undefined): boolean {
  return normalizeStatusPhrase(phrase) === 'created';
}

/** Guidance Gantt / tree status enum. */
export function guidanceStatusFromPhrase(phrase: string | null | undefined): GuidanceNodeStatus {
  if (isDoneStatusPhrase(phrase)) return 'done';
  if (isActiveStatusPhrase(phrase)) return 'active';
  return 'todo';
}

/** Drive artifact life chip. */
export function driveLifecycleFromPhrase(phrase: string | null | undefined): DriveLifecycle {
  if (isDoneStatusPhrase(phrase)) return 'closed';
  if (isActiveStatusPhrase(phrase)) return 'active';
  if (isDraftStatusPhrase(phrase)) return 'drafted';
  return 'created';
}

const STATUS_PILL_MAP: Record<string, string> = {
  initialized: 'Not started',
  drafted: 'Draft',
  active: 'In progress',
  ready: 'Ready',
  open: 'Open',
  closed: 'Complete',
  complete: 'Complete',
  done: 'Complete',
  blocked: 'Blocked',
  deferred: 'Deferred',
};

/** Mission Control / next-move English pill. */
export function statusPillLabelFromPhrase(statusPhrase?: string | null): string {
  if (!statusPhrase?.trim()) return 'Not started';
  const key = normalizeStatusPhrase(statusPhrase);
  return STATUS_PILL_MAP[key] ?? statusPhrase.replace(/_/g, ' ');
}
